import React, { useState, useEffect } from 'react';
import { Box } from '@mui/system';
import { Avatar } from '@mui/material';
import colors from 'styles/colors';
import useAccountStore from 'store/accountStore';
import { useModalContext } from 'components/system/ModalContext';
import useAvatarUpload from 'hooks/useAvatarUpload';
import getColorForName from 'utils/nameColorPicker';
import useGetUserData from 'hooks/useGetUserData';
import AvatarFileButton from './AvatarFileButton';
import AvatarCropper from './AvatarCropper';
import './AvatarSettings.css';

const decreasedQuality = 1; // 60% quality

const AvatarSettings = () => {
  const { firstName, lastName } = useGetUserData();
  const [avatar, setAvatar] = useState(null);
  const { openModal, closeModal } = useModalContext();
  const { mutate: uploadAvatar } = useAvatarUpload();
  const userAvatar = useAccountStore((state) => state.account.avatar);

  const handleAvatarChange = (file) => {
    setAvatar(URL.createObjectURL(file));
  };

  const calculateQuality = (imageSize) => {
    // Start with higher quality for smaller images
    if (imageSize < 2 * 1024 * 1024) return 0.95; // 2MB
    if (imageSize < 5 * 1024 * 1024) return 0.9; // 5MB
    if (imageSize < 10 * 1024 * 1024) return 0.85; // 10MB
    return 0.8; // for very large images
  };

  const processImage = async (croppedAreaPixels, maxSizeMB = 1, maxDimension = 800) => {
    const croppedBlob = await getCroppedImage(croppedAreaPixels);
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    return new Promise((resolve) => {
      let quality = calculateQuality(croppedBlob.size);
      const attemptResize = (blob, attempts = 0) => {
        if (attempts > 3) {
          // If we've tried 3 times and still can't get the right size,
          // just return the last attempt
          resolve(convertToBase64(blob));
          return;
        }

        resizeImage(blob, maxDimension, quality, (resizedBlob) => {
          if (resizedBlob.size <= maxSizeBytes) {
            convertToBase64(resizedBlob).then(resolve);
          } else {
            // Reduce quality more gradually
            quality *= 0.9;
            attemptResize(blob, attempts + 1);
          }
        });
      };

      attemptResize(croppedBlob);
    });
  };

  const resizeImage = (file, maxDimension, quality, callback) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      const img = new Image();
      img.onload = function () {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate new dimensions while maintaining aspect ratio
        const { width: newWidth, height: newHeight } = calculateDimensions(
          img.width,
          img.height,
          maxDimension,
        );

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Optional: Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (blob) => callback(blob),
          'image/jpeg',
          quality,
        );
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const calculateDimensions = (width, height, maxDimension) => {
    if (width <= maxDimension && height <= maxDimension) {
      return { width, height };
    }

    if (width > height) {
      const ratio = height / width;
      return {
        width: maxDimension,
        height: Math.round(maxDimension * ratio),
      };
    }
    const ratio = width / height;
    return {
      width: Math.round(maxDimension * ratio),
      height: maxDimension,
    };
  };

  // Convert Blob to Base64
  const convertToBase64 = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  const getCroppedImage = async (croppedAreaPixels, maxDimension = 800) => {
    const image = new Image();
    image.src = avatar;

    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
        );

        // Check if resizing is needed
        let newWidth = croppedAreaPixels.width;
        let newHeight = croppedAreaPixels.height;

        if (newWidth > maxDimension || newHeight > maxDimension) {
          // Resize only if larger than maxDimension
          if (newWidth > newHeight) {
            newWidth = maxDimension;
            newHeight = Math.round((croppedAreaPixels.height / croppedAreaPixels.width) * maxDimension);
          } else {
            newHeight = maxDimension;
            newWidth = Math.round((croppedAreaPixels.width / croppedAreaPixels.height) * maxDimension);
          }

          // Create new canvas for resized image
          const resizedCanvas = document.createElement('canvas');
          const resizedCtx = resizedCanvas.getContext('2d');

          resizedCanvas.width = newWidth;
          resizedCanvas.height = newHeight;
          resizedCtx.drawImage(canvas, 0, 0, newWidth, newHeight);

          // Convert resized image to Blob
          resizedCanvas.toBlob(
            (blob) => resolve(blob),
            'image/jpeg',
            decreasedQuality,
          );
        } else {
          canvas.toBlob(
            (blob) => resolve(blob),
            'image/jpeg',
            decreasedQuality,
          );
        }
      };
    });
  };

  const handleUpload = async (croppedAreaPixels) => {
    // crop, resize and downgrade image quality, so it doesn't exceed 1MB
    const processedImage = await processImage(croppedAreaPixels);
    if (processedImage) {
      uploadAvatar(processedImage);
    }
  };

  useEffect(() => {
    if (avatar) {
      openModal({
        content: (
          <AvatarCropper
            imageSrc={avatar}
            onConfirm={async (croppedAreaPixels) => {
              await handleUpload(croppedAreaPixels);
              closeModal();
            }}
          />
        ),
      });
    }
  }, [avatar]);

  return (
    <Box
      sx={{
        padding: '24px 0',
        borderBottom: `1px solid ${colors.border.border_5p}`,
        position: 'relative',
      }}
    >
      <Avatar
        src={userAvatar}
        alt="User Avatar"
        style={{
          width: '200px',
          height: '200px',
          borderRadius: '200px',
          fontSize: '60px',
          color: colors.text.primary,
          backgroundColor: getColorForName(firstName, lastName),
          textTransform: 'capitalize',
        }}
      >
        {firstName[0]}
      </Avatar>
      <AvatarFileButton hasAvatar={Boolean(userAvatar)} onImageChange={handleAvatarChange} />
    </Box>
  );
};

export default AvatarSettings;
